import moment from 'moment'
import { convertUrlFiltersToES, encodeFilters, FilterObject } from 'app/filter.utils'
import { ContactJson } from 'app/types/entities'
import { QueueView } from './boardView'
import { queueViewPath } from './QueueViewToggle'
import { type TreatmentBand, bandSearchTerms, treatmentBand } from './treatmentBands'

/**
 * Declarative registry of Action Center work queues.
 *
 * Each queue's base filters MUST stay in parity with the matching tab in
 * `useContactsPage.tsx` and the count filters in `useSidebarCounts.tsx` — the
 * Action Center count, the sidebar count, and the drill-through table all have
 * to agree on what "the queue" is. (Extracting those two files onto this
 * registry is the planned integration step; for now the definitions are
 * mirrored here.)
 */

export type QueueValueStats = {
  /** Avg production per new-patient visit — workspace, trailing 12 months. */
  avgNewPatientVisitValue?: number
  /** Avg net production per visit (all visit types) — workspace, trailing 12 months. */
  avgProductionPerVisit?: number
}

export type QueueSearchOpts = {
  needsActionDateEnabled: boolean
  /** Keep the already booked in the hygiene windows: the Hygiene Recall list's Scheduled stage holds them. */
  includeBooked?: boolean
}

export type QueueFreshnessSegment = {
  id: string
  label: string
  /** The window the label leaves unsaid, shown on hover. */
  description: string
  /** Extra ES terms ANDed onto the queue's base terms to count this segment. */
  extraTerms: (opts: QueueSearchOpts) => string[]
}

/**
 * Which side of the business a module works. Colour is assigned by family, not
 * per module: every lead module is blue and every current-patient module is
 * green, so a new module inherits the right colour instead of picking one.
 */
export type QueueFamily = `lead` | `patient`

export const FAMILY_ACCENT: Record<QueueFamily, string> = {
  lead: `bg-blue-100 text-blue-700`,
  patient: `bg-green-100 text-green-700`,
}

/** The card's wash, same family colour fading out down the card. */
export const FAMILY_TINT: Record<QueueFamily, string> = {
  lead: `from-blue-500/[0.07]`,
  patient: `from-green-500/[0.07]`,
}

/**
 * Modules that are one workflow split into timeframes.
 *
 * The hygiene bands exist because a single overdue pile 2,483 deep was
 * unfinishable, and splitting it by how late someone is made each band both
 * workable and a different conversation. But three cards side by side implied
 * three separate jobs, when a practice thinks of it as one: recall. They stay
 * three queues with their own counts and their own entry points, presented as
 * one card.
 */
export type QueueBundleId = `hygiene` | `treatment`

export const QUEUE_BUNDLES: Record<QueueBundleId, { name: string; strapline: string }> = {
  hygiene: {
    name: `Hygiene recall`,
    strapline: `Everyone due a cleaning, split by how late they are`,
  },
  treatment: {
    name: `Unscheduled treatment`,
    strapline: `Planned work nobody booked, split by how long ago it was planned`,
  },
}

export type ActionQueueDef = {
  id:
    | `active-leads`
    | `due-for-hygiene`
    | `hygiene-coming-due`
    | `hygiene-long-past-due`
    | `treatment-fresh`
    | `treatment-cooling`
    | `treatment-cold`
    | `treatment-stale`
  name: string
  /** One line under the queue name answering why this work matters. */
  strapline: string
  /**
   * What is actually in this slice, for the line under its name inside a
   * bundle. Distinct from `strapline`, which sells the work: on a bundle the
   * three entries differ only by their window, so the useful thing to say next
   * to a count somebody is about to click is which window it is.
   */
  definition?: string
  /** Presented alongside its siblings under one heading, rather than alone. */
  bundle?: QueueBundleId
  /** Name for use inside a bundle, where the shared subject is already stated. */
  shortName?: string
  /** Tailwind classes for the icon tile (bg + text). */
  family: QueueFamily
  /**
   * How the module is worked. Most queues hand you one contact at a time, which
   * suits work that resolves in a single call. Treatment does not: a case worth
   * thousands takes a conversation over days, so it gets a board where several
   * stay in flight at once.
   */
  surface?: `focus` | `board` | `both`
  /**
   * Where a `both` queue opens before anyone has chosen for themselves. Not a
   * restriction on what the queue offers, since both views stay reachable
   * either way. It changes only which one greets a person the first time,
   * which is a fact about the queue's natural home rather than a fact about
   * how preferences are stored. Unset means the focus card, the gentler
   * first impression for work that resolves in a single call.
   */
  defaultView?: QueueView
  /**
   * Where a board's columns come from, and where a move is written. Absent on
   * queues that resolve in a single call and have nothing to track between
   * conversations, which is most of them.
   */
  board?: { source: `lead-status` } | { source: `pipeline`; pipelineId: string }
  /**
   * Contacts waiting, for modules whose count cannot come from a contact
   * search. Unscheduled treatment lives on procedure records, which the search
   * index does not carry yet (TRK-5482).
   */
  stagedCount?: () => number
  searchTerms: (opts: QueueSearchOpts) => string[]
  sort: (opts: QueueSearchOpts) => { field: string; direction: `ASC` | `DESC` }
  /** Deep link to the existing filtered table view. */
  drillHref: string
  /** Estimated production value of working one contact in this queue. */
  perItemValue: (stats: QueueValueStats) => number | undefined
  /** What one unit of that value represents, e.g. "per new patient". */
  valueUnit: string
  /** Plain-language explanation of how the figure is derived. */
  valueBasis: string
  /** Extra story facts for the play panel (the play's own detail comes first). */
  whyLine?: (contact: ContactJson) => string
  freshness: QueueFreshnessSegment[]
  /** How many of the queue's first contacts, in its own sort, the card lists. */
  previewCount?: number
}

function filtersToTerms(filters: FilterObject[]): string[] {
  return convertUrlFiltersToES(encodeFilters(filters))
}

const day = (offsetDays: number) => moment().subtract(offsetDays, `days`).format(`YYYY-MM-DD`)

/** Non-perio hygiene interval (days). The queue filters out perio patients. */
const PROPHY_INTERVAL_DAYS = 181

/**
 * Hygiene dates are date-only values stored at UTC midnight, so every read of
 * one has to be UTC. Parsing locally shifts the day backwards anywhere west of
 * Greenwich: a Feb 11 cleaning rendered as "Feb 10" and shifted the due date
 * with it.
 */
function hygieneDay(contact: ContactJson) {
  return contact.lastHygieneDate ? moment.utc(contact.lastHygieneDate) : undefined
}

/** When this contact's next cleaning comes due, as a UTC date-only ISO string. */
export function hygieneDueOn(contact: ContactJson): string | undefined {
  const last = hygieneDay(contact)
  return last ? last.clone().add(PROPHY_INTERVAL_DAYS, `days`).format(`YYYY-MM-DD`) : undefined
}

/** How many days until this contact's next cleaning comes due. */
export function hygieneDaysUntilDue(contact: ContactJson): number | undefined {
  const last = hygieneDay(contact)
  if (!last) return undefined
  const due = last.clone().add(PROPHY_INTERVAL_DAYS, `days`).startOf(`day`)
  return due.diff(moment.utc().startOf(`day`), `days`)
}

export function hygieneMonthsOverdue(contact: ContactJson): number | undefined {
  const last = hygieneDay(contact)
  if (!last) return undefined
  const due = last.clone().add(PROPHY_INTERVAL_DAYS, `days`)
  const months = moment.utc().diff(due, `months`)
  return months < 0 ? 0 : months
}

/**
 * A "play" — the contextual treatment of a focus card for one Needs Action
 * reason. Reasons mirror the backend's `Contact.calculateLeadStatus` triggers
 * (trackable-api `Contact.ts`): a broken appointment, an unlinked/unscheduled
 * patient, or a fresh inbound lead tag (`New *` / `Unscheduled *`). Ordered by
 * priority; first matching tag wins.
 */
export type ActionPlay = {
  /** Headline reason — the card's lede. */
  label: string
  /** Visual tone of the reason panel (maps to the Badge palette tints). */
  tone: `danger` | `blue` | `amber` | `gray` | `green`
  /** Which action the play leads with. */
  primary: `call` | `text` | `profile`
  /** Contextual fact derived from contact fields, e.g. "Missed visit Aug 3". */
  detail?: (contact: ContactJson) => string | undefined
  /** Short imperative next step, e.g. "Call to rebook". */
  next: string
  /**
   * Appointment status backing this play, e.g. `Broken`. The card looks up the
   * real appointment record with that status and renders it as a date tile, and
   * a play with its own appointment evidence suppresses the generic last-call
   * block. The app's appointment vocabulary is Broken / Completed / Scheduled;
   * there is no cancelled status, so the canceled play has none to name.
   */
  appointmentStatus?: string
  /**
   * Render the recall timeline instead of describing the dates in words. The
   * interval between the last cleaning and the next due date IS the story on a
   * hygiene card, and a rail shows it faster than a sentence.
   */
  timeline?: `hygiene`
  /**
   * For activity-triggered reasons: the activity types that put the contact in
   * Needs Action. Focus mode fetches the latest matching activity and shows it
   * on the card.
   */
  triggerActivityTypes?: string[]
}

/**
 * Fallback wording for plays with no appointment record to point at. Uses the
 * contact's denormalized pointer, which carries no status, so it stays a
 * best-effort sentence rather than something the card presents as evidence.
 */
const missedVisitDetail = (verb: string) => (contact: ContactJson) => {
  const date = contact.nearestAppointmentDate
  if (!date || !moment.utc(date).isBefore(moment(), `day`)) return undefined
  return `${verb} ${moment.utc(date).format(`MMM D`)}`
}

const calledDetail = (contact: ContactJson) =>
  contact.lastActivityDate ? `They called ${moment(contact.lastActivityDate).fromNow()}` : undefined

const inboundDetail = (contact: ContactJson) => {
  const submitted = contact.firstActivityDate ?? contact.becameLeadDate
  return [
    contact.firstContactReason ? `Asked about "${contact.firstContactReason}"` : undefined,
    submitted ? `submitted ${moment(submitted).fromNow()}` : undefined,
  ]
    .filter(Boolean)
    .join(` · `)
}

const LEAD_PLAYS: { tag: string; play: ActionPlay }[] = [
  {
    tag: `Broken Appointment`,
    play: {
      label: `Broken appointment`,
      tone: `danger`,
      primary: `call`,
      // The date comes from the broken appointment record, so the sentence
      // does not repeat it.
      detail: () => `Reach out and get them rescheduled`,
      appointmentStatus: `Broken`,
      next: `Call to rebook`,
    },
  },
  {
    tag: `Canceled Appointment`,
    play: {
      label: `Canceled appointment`,
      tone: `danger`,
      primary: `call`,
      detail: missedVisitDetail(`Canceled their visit`),
      next: `Call to rebook`,
    },
  },
  {
    tag: `No Patient Linked`,
    play: {
      label: `No PMS patient matched`,
      tone: `gray`,
      primary: `profile`,
      next: `Open the profile and link their patient record`,
    },
  },
  {
    tag: `No Appointment`,
    play: {
      label: `Patient linked, nothing scheduled`,
      tone: `amber`,
      primary: `call`,
      next: `Call to get them scheduled`,
    },
  },
  {
    tag: `Unscheduled Call`,
    play: {
      label: `Called in but didn't schedule`,
      tone: `amber`,
      primary: `call`,
      detail: calledDetail,
      next: `Call back while it's warm`,
      triggerActivityTypes: [`Call`],
    },
  },
  {
    tag: `New Form`,
    play: {
      label: `New form submission`,
      tone: `blue`,
      primary: `call`,
      detail: inboundDetail,
      next: `Call while it's warm`,
      triggerActivityTypes: [`Form`],
    },
  },
  {
    tag: `New Chat`,
    play: {
      label: `New chat lead`,
      tone: `blue`,
      primary: `call`,
      detail: inboundDetail,
      next: `Call while it's warm`,
      triggerActivityTypes: [`Chat`],
    },
  },
  {
    tag: `New Online Scheduler`,
    play: {
      label: `Online scheduling request`,
      tone: `blue`,
      primary: `call`,
      detail: inboundDetail,
      next: `Call to close the loop`,
      triggerActivityTypes: [`Online Scheduler`, `Scheduler`],
    },
  },
  {
    tag: `New Virtual Consultation`,
    play: {
      label: `Virtual consultation request`,
      tone: `blue`,
      primary: `call`,
      detail: inboundDetail,
      next: `Call to confirm a time`,
      triggerActivityTypes: [`Virtual Consultation`],
    },
  },
  {
    tag: `Missed Call`,
    play: {
      label: `Missed their call`,
      tone: `amber`,
      primary: `call`,
      detail: calledDetail,
      next: `Return the call`,
      triggerActivityTypes: [`Call`],
    },
  },
  {
    tag: `Callback`,
    play: {
      label: `Callback requested`,
      tone: `amber`,
      primary: `call`,
      detail: calledDetail,
      next: `Call back, they asked for it`,
      triggerActivityTypes: [`Call`],
    },
  },
  {
    tag: `Voicemail`,
    play: {
      label: `Left a voicemail`,
      tone: `amber`,
      primary: `call`,
      detail: calledDetail,
      next: `Listen, then call back`,
      triggerActivityTypes: [`Call`],
    },
  },
  {
    tag: `Appointment Not Scheduled`,
    play: {
      label: `Appointment not scheduled`,
      tone: `amber`,
      primary: `call`,
      next: `Call to lock in a time`,
      triggerActivityTypes: [`Call`],
    },
  },
]

const DEFAULT_LEAD_PLAY: ActionPlay = {
  label: `Needs a touch`,
  tone: `gray`,
  primary: `text`,
  next: `Review the profile, then reach out`,
}

const HYGIENE_UPCOMING_PLAY: ActionPlay = {
  label: `Cleaning coming due`,
  tone: `blue`,
  primary: `text`,
  // The timeline carries the countdown, so there is no sentence to repeat it.
  timeline: `hygiene`,
  next: `Text to offer a time`,
}

const HYGIENE_PLAY: ActionPlay = {
  label: `Overdue for a cleaning`,
  // Late is late. A green panel wrapped around a red overdue timeline was the
  // card saying two opposite things at once; the state gets one colour.
  tone: `danger`,
  primary: `text`,
  timeline: `hygiene`,
  next: `Text to offer a time`,
}

/** Every hygiene recall queue id, so a card gets the hygiene play regardless of which one it came through. */
const HYGIENE_QUEUE_IDS = new Set([`due-for-hygiene`, `hygiene-coming-due`, `hygiene-long-past-due`])

export function actionPlayFor(queueId: string, contact: ContactJson): ActionPlay {
  if (HYGIENE_QUEUE_IDS.has(queueId)) {
    // Overdue-or-not comes from the contact's own due date, not from which of
    // the three queue ids was passed in. The Hygiene Recall board (see
    // ContactBoardComponent.tsx) mounts every band, including "All", off one
    // shared `queueId` (`due-for-hygiene`, its anchor `ActionQueueDef`), so a
    // card whose contact is not actually overdue would otherwise get labelled
    // "Overdue for a cleaning" just because the board's anchor id says so.
    // `hygieneDaysUntilDue` is safe to read for every card this branch ever
    // sees: all three queues already exclude perio patients, so the interval
    // it assumes (`PROPHY_INTERVAL_DAYS`) is always the right one.
    const untilDue = hygieneDaysUntilDue(contact)
    return untilDue != null && untilDue > 0 ? HYGIENE_UPCOMING_PLAY : HYGIENE_PLAY
  }
  const tags = contact.tags ?? []
  const match = LEAD_PLAYS.find((entry) => tags.includes(entry.tag))
  if (match) return match.play
  if (contact.appointmentStatus === `Broken`) return LEAD_PLAYS[0].play
  if (!contact.lastCommDate) return { ...DEFAULT_LEAD_PLAY, label: `No contact logged yet` }
  return DEFAULT_LEAD_PLAY
}

const activeLeads: ActionQueueDef = {
  id: `active-leads`,
  name: `Active Leads`,
  strapline: `Speed to first touch wins the booking`,
  family: `lead`,
  surface: `both`,
  defaultView: `table`,
  // Not a pipeline. Leads already carry a persisted status that this queue
  // filters on, and 2,376 of them have one while zero are in any pipeline, so a
  // pipeline-backed board would open empty.
  board: { source: `lead-status` },
  searchTerms: ({ needsActionDateEnabled }) => {
    const dateField = needsActionDateEnabled ? `needsActionDate` : `becameLeadDate`
    return filtersToTerms([
      { attribute: `leadStatus.keyword`, value: [`Needs Action`, `In Progress`], operator: `in` },
      { attribute: dateField, value: `last_30_days`, operator: `between` },
      { attribute: `patientStatus`, value: [`NonPatient`], operator: `not_in` },
    ] as FilterObject[])
  },
  sort: ({ needsActionDateEnabled }) => ({
    // Speed-to-lead: newest first — fresh leads convert best.
    field: needsActionDateEnabled ? `needsActionDate` : `becameLeadDate`,
    direction: `DESC`,
  }),
  drillHref: `/contacts`,
  // A lead who books becomes a patient, so the honest unit is what a patient
  // produces over their life, not what one visit bills.
  perItemValue: (stats) => stats.avgNewPatientVisitValue,
  valueUnit: `per new patient`,
  valueBasis: `Your average production from a new patient's first visit, over the last 12 months.`,
  // The queue's own window date, so "new" is always a subset of "waiting". Rolling
  // 24 hours, not a calendar day, so overnight leads still count in the morning.
  freshness: [
    {
      id: `new`,
      label: `new`,
      description: `Joined Active Leads in the last 24 hours`,
      extraTerms: ({ needsActionDateEnabled }) => [
        `${needsActionDateEnabled ? `needsActionDate` : `becameLeadDate`}:[now-24h TO *]`,
      ],
    },
  ],
  // As many rows as fit without growing past the My tasks card beside it
  // (a fixed h-96); a fourth row would stretch the whole row.
  previewCount: 3,
}

const dueForHygiene: ActionQueueDef = {
  id: `due-for-hygiene`,
  name: `Just past due for Hygiene (30 days)`,
  strapline: `Barely late, and the easiest to bring back`,
  definition: `Came due in the last 30 days`,
  bundle: `hygiene`,
  // The exact window, not "Just past due": a vague label reads as a wrong
  // label the moment the board's own control shows two more of them.
  shortName: `0 to 30 days overdue`,
  family: `patient`,
  surface: `both`,
  board: { source: `pipeline`, pipelineId: `system:hygiene` },
  // Came due within the last 30 days. The highest-recovery window there is:
  // they still have the practice in mind and nothing has hardened into a habit
  // of not going.
  searchTerms: (opts) => hygieneWindow(-30, 0, opts),
  // Freshest-due first: recently-lapsed patients are the likeliest to rebook.
  sort: () => ({ field: `lastHygieneDate`, direction: `DESC` }),
  drillHref: `/contacts?tab=due-for-hygiene`,
  // No figure until the API can price a hygiene visit. The only per-visit
  // average it exposes is `avgNetProductionPerVisit`, taken across every visit
  // type, so it is pulled up by crowns and implants and overstates what a
  // recall call is worth. Overstating production on a number staff might repeat
  // to an owner is worse than showing none, and the card already reads fine
  // without one. Restore this the day a hygiene-specific average exists.
  perItemValue: () => undefined,
  valueUnit: `per visit`,
  valueBasis: `Not shown: the API has no hygiene-specific production average yet.`,
  // No whyLine: the timeline says everything this panel needs to say.
  // No freshness badges: the aging split read as noise next to the headline
  // count, and the queue's freshest-due-first ordering already surfaces the
  // recently-lapsed patients first.
  freshness: [],
}

export function hygieneWindow(
  fromDueDays: number,
  toDueDays: number,
  { includeBooked = false }: Pick<QueueSearchOpts, `includeBooked`> = {},
): string[] {
  return [
    ...filtersToTerms([
      { attribute: `patientStatus`, value: [`NonPatient`, `Inactive`, `Archived`], operator: `not_in` },
      ...(includeBooked ? [] : [{ attribute: `appointmentStatus.keyword`, value: [`Scheduled`], operator: `not_in` }]),
      {
        attribute: `hygieneDueDate`,
        value: [dueDay(fromDueDays), dueDay(toDueDays)],
        operator: `between`,
      },
      { attribute: `birthDate`, value: `18`, operator: `greater_equal_than` },
      { attribute: `isPerio`, value: [`false`], operator: `in` },
    ] as FilterObject[]),
    // Nobody who already has a chair.
    //
    // `appointmentStatus` was supposed to carry this, and mostly does not:
    // it is null on plenty of records that hold a future appointment, so the
    // rule above let them straight through. Measured on one practice, the
    // queue was 44% people who had already booked for the window that came
    // due in the last month, and 85% for the month ahead. A recall list where
    // five of every six calls are to somebody with a cleaning in the diary is
    // not a recall list.
    //
    // Written by hand rather than through `filtersToTerms` because the
    // filter vocabulary has no "not in a date range", and expressed as a
    // negated range because a contact with no appointment at all has no
    // `nearestAppointmentDate` to match, and a missing field never matches a
    // range: inverting it keeps them, which is the whole point.
    //
    // The worry that this also hides somebody whose only future appointment is
    // one they BROKE was measured once and dismissed, on the reasoning that an
    // appointment is marked broken after its time has passed so a broken one
    // always sits in the past. That does not hold everywhere. Measured again on
    // Braselton Smiles Studio (2026-09-01): of 72 contacts in this window
    // carrying a future appointment, 69 read `Scheduled` and the other 3 read
    // Broken or Cancelled. The plain negation removed all 72, so every contact
    // it took off the queue beyond what `appointmentStatus` had already excluded
    // was a broken or cancelled one — precisely the people a recall list exists
    // to chase.
    //
    // Hence the carve-out, which the earlier note recorded as impossible: the
    // form it tried, `(!nearest... OR appointmentStatus:("Broken"))`, leads its
    // OR group with a negation. Such a group has no positive clause to subtract
    // from and collapses rather than erroring (it returned 811 rows here where
    // the plain negation returned 1,270). Anchoring the group on the positive
    // clause and giving the negation an explicit `*:*` to subtract from parses
    // correctly.
    //
    // Verified on the same data: population 1,342, of whom 72 hold a future
    // appointment (69 genuinely booked, 3 broken/cancelled). Plain negation
    // returns 1,270 (drops all 72); this form returns 1,273 (drops exactly the
    // 69). A practice whose `appointmentStatus` really is null on booked records
    // still gets those filtered, because the negation half is unchanged.
    //
    // Deleted is carved out too: an appointment taken off the schedule keeps its
    // date. Braselton (2026-10-08) had 22 due patients hidden behind one.
    ...(includeBooked
      ? []
      : [
          `(appointmentStatus:("Broken" OR "Cancelled" OR "Deleted") OR (*:* !nearestAppointmentDate:[${dueDay(0)} TO *]))`,
        ]),
  ]
}

/** A due-date offset from today as a UTC date-only string. */
function dueDay(days: number): string {
  return moment.utc().add(days, `days`).format(`YYYY-MM-DD`)
}

/** How far ahead the coming-due queue looks. */
const UPCOMING_HYGIENE_DAYS = 30

/**
 * The preventative sibling of Due for Hygiene: patients whose recall lands in
 * the next month, caught before they lapse into the overdue pile.
 *
 * Same population and same filters as its sibling, only the window moves.
 */
const comingDueForHygiene: ActionQueueDef = {
  id: `hygiene-coming-due`,
  name: `Due soon for Hygiene (30 days)`,
  definition: `Due in the next 30 days`,
  strapline: `Book them before they lapse`,
  bundle: `hygiene`,
  // The exact window, not "Due soon": see `dueForHygiene`'s shortName comment.
  shortName: `Due within 30 days`,
  family: `patient`,
  surface: `both`,
  board: { source: `pipeline`, pipelineId: `system:hygiene` },
  // Through `hygieneWindow` rather than a second copy of the same filters:
  // this queue had drifted out of step with its siblings the moment one of
  // them gained a rule, which is exactly what happened when the already-booked
  // exclusion landed there and not here. Only the `hygieneDueDate` range
  // differs from its siblings (tomorrow through +30 days, vs. their
  // past-facing ranges), which is what lets `hygieneBands.ts`'s "All" search
  // cover all three windows with one `hygieneWindow(-3650, 30)` call.
  searchTerms: (opts) => hygieneWindow(1, UPCOMING_HYGIENE_DAYS, opts),
  // Soonest due first. With perio excluded the interval is a constant 181 days,
  // so the oldest cleaning is also the one coming due first.
  sort: () => ({ field: `lastHygieneDate`, direction: `ASC` }),
  drillHref: `/contacts`,
  // No figure until the API can price a hygiene visit. The only per-visit
  // average it exposes is `avgNetProductionPerVisit`, taken across every visit
  // type, so it is pulled up by crowns and implants and overstates what a
  // recall call is worth. Overstating production on a number staff might repeat
  // to an owner is worse than showing none, and the card already reads fine
  // without one. Restore this the day a hygiene-specific average exists.
  perItemValue: () => undefined,
  valueUnit: `per visit`,
  valueBasis: `Not shown: the API has no hygiene-specific production average yet.`,
  // No whyLine: the timeline says everything this panel needs to say.
  freshness: [],
}

/**
 * Everything that slipped past the near band. Kept as its own module rather
 * than folded back in: a patient a year gone needs a different conversation
 * than one a fortnight late, and mixing them made a single unfinishable list
 * that nobody could start.
 */
const longPastDueForHygiene: ActionQueueDef = {
  id: `hygiene-long-past-due`,
  name: `Long past due for Hygiene`,
  definition: `Came due more than 30 days ago`,
  strapline: `Gone a while, worth a warmer approach`,
  bundle: `hygiene`,
  // The exact window, not "Long past due": see `dueForHygiene`'s shortName
  // comment. "Over 30 days" rather than "31 to 3650 days": the far edge is a
  // data-history artifact (see `hygieneWindow` below), not a real boundary
  // worth putting in front of a front-desk user.
  shortName: `Over 30 days overdue`,
  family: `patient`,
  surface: `both`,
  board: { source: `pipeline`, pipelineId: `system:hygiene` },
  searchTerms: (opts) => hygieneWindow(-3650, -31, opts),
  // Freshest-due first: the least-gone are still the likeliest to come back.
  sort: () => ({ field: `lastHygieneDate`, direction: `DESC` }),
  drillHref: `/contacts?tab=due-for-hygiene`,
  // No figure until the API can price a hygiene visit. The only per-visit
  // average it exposes is `avgNetProductionPerVisit`, taken across every visit
  // type, so it is pulled up by crowns and implants and overstates what a
  // recall call is worth. Overstating production on a number staff might repeat
  // to an owner is worse than showing none, and the card already reads fine
  // without one. Restore this the day a hygiene-specific average exists.
  perItemValue: () => undefined,
  valueUnit: `per visit`,
  valueBasis: `Not shown: the API has no hygiene-specific production average yet.`,
  freshness: [],
}

/**
 * Treatment the practice planned and nobody booked.
 *
 * The largest pot of production in the building, and the only module that does
 * not sit on a contact search: the money and the appointment state live on
 * procedure records, which the search index does not carry yet. Until the sync
 * lands (TRK-5482) the count comes from measured practice data and the board
 * runs on a staged sample.
 *
 * The rule the module encodes, learned from the live data: treatment counts as
 * unbooked when it is pending and has no *live* appointment. An appointment
 * that was cancelled or broken does not count as booked. At Hamblin, 28% of the
 * appointments behind pending treatment were cancelled, so reading "has an
 * appointment" as "booked" would have hidden the warmest cases on the list.
 */
/**
 * One band of unscheduled treatment.
 *
 * Split for the reason hygiene is split: a single pile of everything unbooked
 * is unfinishable, and the age of the case changes what the call has to be.
 * The bands share a board, a value basis and a surface, so they are built from
 * one shape rather than written out three times.
 */
function treatmentQueue(band: TreatmentBand, id: ActionQueueDef[`id`]): ActionQueueDef {
  return {
    id,
    name: band.name,
    strapline: band.strapline,
    definition: band.name,
    bundle: `treatment`,
    shortName: band.shortName,
    family: `patient`,
    surface: `both`,
    // A case worth thousands, tracked over several conversations, belongs on
    // its board from the first click. Due for Hygiene stays unset, so it keeps
    // opening on the gentler one-at-a-time card.
    defaultView: `board`,
    board: { source: `pipeline`, pipelineId: `system:treatment` },
    // A contact search now that the rollups exist, so the API counts it the way
    // it counts hygiene. `stagedCount` is gone with the fixture it read.
    searchTerms: () => bandSearchTerms(band.id),
    sort: () => ({ field: `updatedDate`, direction: `DESC` }),
    // Treatment has no filtered table view to drill into (unlike hygiene's
    // `/contacts?tab=due-for-hygiene`), so this has always pointed at the
    // board itself. Built through `queueViewPath` rather than a literal
    // string so it can never drift from where the primary button already
    // sends the same click.
    drillHref: queueViewPath(`board`, id),
    // No figure until the average can come from the same search the count does.
    // The fixture's average was a sample of 41 cases quoting a practice-wide
    // number, and the honest replacement is an aggregate the API can give us.
    perItemValue: () => undefined,
    valueUnit: `per patient`,
    valueBasis: `Not shown: the average needs an aggregate over the band, not a sample.`,
    freshness: [],
  }
}

const treatmentFresh = treatmentQueue(treatmentBand(`fresh`), `treatment-fresh`)
const treatmentCooling = treatmentQueue(treatmentBand(`cooling`), `treatment-cooling`)
const treatmentCold = treatmentQueue(treatmentBand(`cold`), `treatment-cold`)
const treatmentStale = treatmentQueue(treatmentBand(`stale`), `treatment-stale`)

export const ACTION_QUEUES: ActionQueueDef[] = [
  activeLeads,
  comingDueForHygiene,
  dueForHygiene,
  longPastDueForHygiene,
  treatmentFresh,
  treatmentCooling,
  treatmentCold,
  treatmentStale,
]

/** Queue definitions by id. `get(undefined)` is simply undefined, as callers expect. */
export const ACTION_QUEUE_BY_ID = new Map<string | undefined, ActionQueueDef>(
  ACTION_QUEUES.map((queue) => [queue.id, queue]),
)
